import { EButtonStyleType, EButtonType } from '@common/enums';
import { DEFAULT_SHADOW_SETTINGS } from '@common/types/shadow-settings.type';

import type { CssVars } from '../../../css-vars';
import { shadowValueVar } from '../../../features/shadow/shadow.css-vars';
import type { ColorCssResolver } from '../../form/styles/themed-button.css-vars';
import { getThemedButtonCssVars, getThemedButtonHoverClasses } from '../../form/styles/themed-button.css-vars';
import type { SocialEntity } from '../schemas';

type SocialWidgetData = SocialEntity['data'];

/**
 * CSS custom properties for the social-networks widget — the LESS twin of the
 * former JSS `getStyles`. ONE module shared by both apps; the static rules
 * consuming the variables live in each component's `.less`
 * (`social-view.component.less` / `social.component.less`).
 *
 * The widget renders one shared appearance for every entry (`data.type`,
 * `styleType` and the theme bags are data-level, mirrored onto each item), so
 * — unlike the button widget's per-item maps — the item variables are computed
 * ONCE at the group level. Group-wrapper variables are computed per component
 * (editor: `computed` signals, published renderer: getters — the video-widget
 * pattern) from the lookup table exported here.
 *
 * A `null` value removes the property from the element, so the consuming
 * declaration falls back to its `var()` default — mirroring the omitted-key
 * behaviour of the old JSS object.
 */
export type SocialCssVars = CssVars;

/**
 * Group-wrapper margin. The negative margin offsets the button items' own
 * margins (button appearance only); icon mode and zero blocks use `0`.
 */
export function getSocialGroupMargin(data: SocialWidgetData, isZero: boolean): string {
    if (isZero) {
        return '0';
    }

    return data.type === EButtonType.button ? '-10px' : '0';
}

/**
 * Variables shared by every social entry. Only meaningful for the button
 * appearance — the custom-button colour/border/radius/hover bag plus the
 * optional shadow. Icon entries carry no per-item variables (their size is a
 * group-level var); returns an empty object for them.
 */
/**
 * Variables for the shared social-entry appearance.
 *
 * `resolveColor` is the host's `colorCss` — theme resolution lives in
 * `@sites/site-theme`, an Angular library, and this package stays framework-free.
 */
export function getSocialItemCssVars(data: SocialWidgetData, resolveColor: ColorCssResolver): SocialCssVars {
    if (data.type !== EButtonType.button) {
        return {};
    }

    const vars: CssVars = {};

    if (data.isShadow) {
        const s = data.shadowSettings ?? DEFAULT_SHADOW_SETTINGS;
        vars['--social-shadow'] = shadowValueVar(s);
    }

    if (data.styleType === EButtonStyleType.custom) {
        Object.assign(vars, getThemedButtonCssVars(data, '--social', resolveColor));
    }

    return vars;
}

/**
 * Modifier classes gating the variable-driven rules — needed because an unset
 * custom property would otherwise reset the declaration to its initial value
 * instead of leaving the themed/global styles intact (e.g. `box-shadow:
 * var(--social-shadow)` on a non-shadowed themed button would compute to
 * `none` and clobber the theme). Mirrors the button widget's modifier classes.
 */
export function getSocialItemCssClasses(data: SocialWidgetData): string {
    if (data.type !== EButtonType.button) {
        return '';
    }

    const classes: string[] = [];

    if (data.isShadow) {
        classes.push('social-shadow');
    }

    if (data.styleType === EButtonStyleType.custom) {
        classes.push('social-custom');

        classes.push(...getThemedButtonHoverClasses(data, 'social'));
    }

    return classes.join(' ');
}
